import dayjs, { type Dayjs } from 'dayjs';

function parseServiceTime(raw: string): Dayjs | null {
  const trimmed = raw.trim();
  if (!trimmed || trimmed === '—') return null;

  let d = dayjs(trimmed);
  if (!d.isValid()) {
    const n = Number(trimmed);
    if (Number.isFinite(n)) {
      if (n > 1e12) d = dayjs(n);
      else if (n > 1e9) d = dayjs(n * 1000);
    }
  }
  return d.isValid() ? d : null;
}

/** Absolute calendar date + time, e.g. `23 Jul 2026 09:00` */
export function formatServiceTimeAbsolute(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed || trimmed === '—') return '—';

  const d = parseServiceTime(trimmed);
  if (!d) return trimmed;

  const now = dayjs();
  if (d.isSame(now, 'year')) return d.format('D MMM HH:mm');
  return d.format('D MMM YYYY HH:mm');
}

/** Friendly relative day label only: Today / Tomorrow / Yesterday */
export function formatServiceTimeFriendlyLabel(raw: string): string | null {
  const d = parseServiceTime(raw);
  if (!d) return null;

  const now = dayjs();
  if (d.isSame(now, 'day')) return 'Today';
  if (d.isSame(now.add(1, 'day'), 'day')) return 'Tomorrow';
  if (d.isSame(now.subtract(1, 'day'), 'day')) return 'Yesterday';
  return null;
}

/**
 * Formats order `service_time` (ISO / Unix seconds / ms / raw backend string) for display.
 * Returns absolute date with optional friendly label, e.g. `23 Jul 09:00 · Today`.
 * Falls back to the original string when parsing fails.
 */
export function formatServiceTime(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed || trimmed === '—') return '—';

  const absolute = formatServiceTimeAbsolute(trimmed);
  if (absolute === trimmed && !parseServiceTime(trimmed)) return trimmed;

  const friendly = formatServiceTimeFriendlyLabel(trimmed);
  return friendly ? `${absolute} · ${friendly}` : absolute;
}
